import axios from 'axios'
import * as Environments from '../constants/environments'

/**
 * ReportService API client
 * Handles communication with the ReportService microservice
 */
class ReportServiceApi {
  constructor() {
    this.baseURL = Environments.REPORT_SERVICE_API_BASE_URL || 'https://report-service.local.leakbot.io/api/v2'
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/vnd.api+json'
      }
    })

    this.client.interceptors.request.use(
      config => {
        const token = window.localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      error => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      response => response,
      error => {
        if (error.response) {
          const { status, data } = error.response
          
          if (status === 401) {
            console.error('Authentication failed - redirecting to login')
          } else if (status === 403) {
            console.error('Insufficient permissions for LIR access')
          }
          
          if (data && data.errors && data.errors.length > 0) {
            const apiError = data.errors[0]
            return Promise.reject({
              status: apiError.status,
              title: apiError.title,
              detail: apiError.detail
            })
          }
        }
        
        return Promise.reject(error)
      }
    )
  }

  /**
   * Build filter parameters from filters object
   * @param {Object} filters - Filter parameters
   * @returns {Object} Query parameters object
   */
  buildFilterParams(filters = {}) {
    const params = {}

    if (filters.partnerReference) {
      params.partnerReference = filters.partnerReference
    }

    if (filters.workOrderNumber) {
      params.workOrderNumber = filters.workOrderNumber
    }
    if (filters.leakbotId) {
      params.leakbotId = filters.leakbotId
    }
    if (filters.dateFrom) {
      params.dateFrom = filters.dateFrom instanceof Date 
        ? filters.dateFrom.toISOString().split('T')[0]
        : filters.dateFrom
    }
    if (filters.dateTo) {
      params.dateTo = filters.dateTo instanceof Date 
        ? filters.dateTo.toISOString().split('T')[0]
        : filters.dateTo
    }
    if (filters.tenantId) {
      params.tenantId = filters.tenantId
    }

    return params
  }

  /**
   * Build sort parameters from sortParams object
   * @param {Object} sortParams - Sort parameters
   * @returns {Object} Query parameters object
   */
  buildSortParams(sortParams = {}) {
    const params = {}

    if (sortParams.field) {
      params['sort[field]'] = sortParams.field
    }
    if (sortParams.direction) {
      params['sort[direction]'] = sortParams.direction
    }

    return params
  }

  /**
   * Get paginated list of LIRs with optional filtering
   * @param {Object} filters - Filter parameters
   * @param {string} filters.partnerReference - Filter by Partner Reference
   * @param {string} filters.workOrderNumber - Filter by work order number
   * @param {string} filters.leakbotId - Filter by LeakBot ID
   * @param {Date} filters.dateFrom - Filter by date from
   * @param {Date} filters.dateTo - Filter by date to
   * @param {number} filters.tenantId - Filter by tenant ID
   * @param {number} page - Page number (1-based)
   * @param {number} limit - Items per page
   * @param {Object} sortParams - Sort parameters
   * @param {string} sortParams.field - Sort field
   * @param {string} sortParams.direction - Sort direction ('asc' or 'desc')
   * @returns {Promise} API response with JSON:API formatted data
   */
  async getLeakInvestigationReports(filters = {}, page = 1, limit = 20, sortParams = {}) {
    const params = {
      'page[number]': page,
      'page[size]': limit,
      ...this.buildFilterParams(filters),
      ...this.buildSortParams(sortParams)
    }

    const response = await this.client.get('/reports/leak-investigation-reports', { params })
    return response.data
  }

  /**
   * Get single LIR by work order GUID
   * @param {string} workOrderGuid - Work order GUID
   * @returns {Promise} API response with JSON:API formatted data
   */
  async getLeakInvestigationReport(workOrderGuid) {
    if (!workOrderGuid) {
      throw new Error('Work Order GUID is required')
    }

    const response = await this.client.get(`/reports/leak-investigation-reports/${workOrderGuid}`)
    return response.data
  }

  /**
   * Get signed download URL for LIR PDF
   * @param {string} workOrderGuid - Work order GUID
   * @returns {Promise} API response with download information
   */
  async getLeakInvestigationReportDownloadUrl(workOrderGuid) {
    if (!workOrderGuid) {
      throw new Error('Work Order GUID is required')
    }

    const response = await this.client.get(`/reports/leak-investigation-reports/${workOrderGuid}/download`)
    return response.data
  }

  /**
   * Download PDF directly (triggers browser download)
   * @param {string} workOrderGuid - Work order GUID
   * @returns {Promise} Download URL for immediate use
   */
  async downloadLeakInvestigationReport(workOrderGuid) {
    const downloadData = await this.getLeakInvestigationReportDownloadUrl(workOrderGuid)
    
    if (downloadData && downloadData.data && downloadData.data.attributes.url) {
      window.open(downloadData.data.attributes.url, '_blank')
      return downloadData.data.attributes.url
    } else {
      throw new Error('Invalid download response format')
    }
  }

  /**
   * Export filtered LIRs as CSV
   * @param {Object} filters - Filter parameters (same as getLeakInvestigationReports)
   * @param {Object} sortParams - Sort parameters
   * @returns {Promise} Triggers CSV download
   */
  async exportLeakInvestigationReports(filters = {}, sortParams = {}) {
    const params = {
      ...this.buildFilterParams(filters),
      ...this.buildSortParams(sortParams)
    }

    try {
      const response = await this.client.get('/reports/leak-investigation-reports/export', {
        params,
        responseType: 'blob' // Important for handling CSV file download
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      
      const contentDisposition = response.headers['content-disposition']
      let filename = 'leak_investigation_reports_export.csv'
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      // Create temporary download link and trigger download
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      return { success: true, filename }
    } catch (error) {
      console.error('CSV export error:', error)
      throw error
    }
  }

  /**
   * Parse JSON:API response data into more usable format
   * @param {Object} jsonApiResponse - JSON:API formatted response
   * @returns {Object} Parsed data with meta information
   */
  parseJsonApiResponse(jsonApiResponse) {
    const result = {
      data: [],
      meta: {
        page: {
          current: 1,
          size: 20,
          total: 0,
          count: 1
        }
      }
    }

    if (!jsonApiResponse) {
      return result
    }

    // Handle single resource or collection
    if (jsonApiResponse.data) {
      if (Array.isArray(jsonApiResponse.data)) {
        result.data = jsonApiResponse.data.map(item => ({
          id: item.id,
          ...item.attributes
        }))
      } else {
        result.data = {
          id: jsonApiResponse.data.id,
          ...jsonApiResponse.data.attributes
        }
      }
    }

    if (jsonApiResponse.meta && jsonApiResponse.meta.page) {
      const pageInfo = jsonApiResponse.meta.page
      result.meta.page = {
        current: pageInfo.current || 1,
        size: pageInfo.size || 20,
        total: pageInfo.total || 0,
        count: pageInfo.count || Math.ceil((pageInfo.total || 0) / (pageInfo.size || 20))
      }
    }

    return result
  }
}

const reportServiceApi = new ReportServiceApi()

export default reportServiceApi